import { getStoredAuthToken } from 'modules/auth/api/authService'

type AuditLayoutStateLike = {
  user?: any
  roles?: any[]
}

const normalizeAuditToken = (value: unknown) => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim().toLowerCase()
  return trimmed.length > 0 ? trimmed : null
}

const readJwtPayload = () => {
  const token = getStoredAuthToken()
  if (!token) return null

  const segments = token.split('.')
  if (segments.length < 2) return null

  try {
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    const decoded = atob(padded)
    return JSON.parse(decoded) as Record<string, unknown>
  } catch {
    return null
  }
}

const addNormalized = (target: Set<string>, value: unknown) => {
  const normalized = normalizeAuditToken(value)
  if (normalized) {
    target.add(normalized)
  }
}

export const collectAuditRoleTokens = (state: AuditLayoutStateLike) => {
  const tokens = new Set<string>()
  const payload = readJwtPayload()

  if (Array.isArray(state.roles)) {
    state.roles.forEach((role) => {
      addNormalized(tokens, role?.roleId)
      addNormalized(tokens, role?.role_id)
      addNormalized(tokens, role?.id)
      addNormalized(tokens, role?.code)
      addNormalized(tokens, role?.displayName)
      addNormalized(tokens, role?.name)
    })
  }

  addNormalized(tokens, state.user?.role)
  addNormalized(tokens, state.user?.roleId)
  addNormalized(tokens, state.user?.role_id)
  addNormalized(tokens, payload?.RoleId)
  addNormalized(tokens, payload?.roleId)
  addNormalized(tokens, payload?.role)

  return tokens
}

export const collectAuditUserIdentityTokens = (state: AuditLayoutStateLike) => {
  const tokens = new Set<string>()
  const payload = readJwtPayload()

  ;[
    state.user?.id,
    state.user?.userId,
    state.user?.user_id,
    state.user?.empId,
    state.user?.emp_id,
    state.user?.employeeId,
    state.user?.employee_id,
    state.user?.username,
    payload?.UserId,
    payload?.userId,
    payload?.EmpId,
    payload?.empId,
    payload?.username,
    payload?.unique_name,
    payload?.sub,
  ].forEach((candidate) => addNormalized(tokens, candidate))

  return tokens
}

export const collectAuditPlantTokens = (state: AuditLayoutStateLike) => {
  const tokens = new Set<string>()
  const payload = readJwtPayload()

  ;[
    state.user?.OrgShortD,
    state.user?.orgShortD,
    state.user?.org_short_d,
    state.user?.plant,
    state.user?.Plant,
    payload?.OrgShortD,
    payload?.orgShortD,
    payload?.Plant,
    payload?.plant,
  ].forEach((candidate) => addNormalized(tokens, candidate))

  return tokens
}

// Roles whose Permit Audit access is scoped to specific departments/locations
// (Area Owner, Area Operator, Safety Area) rather than global. The menu entry
// that grants a user access to /permit-audit is often a *different* role
// (e.g. 30001 Permit Request or 10001 Admin, per role_menu) with no
// department attached - so resolveAuditAccessibleLocationIds must scan for
// these role codes directly instead of scoping to whichever role unlocked
// the menu, or a user's location-scoped role goes undetected.
const AREA_OWNER_ROLE_CODES = new Set(['30003', '30031', '20001'])

export const isAuditAreaOwnerRole = (tokens: Set<string>) =>
  Array.from(tokens).some((token) => AREA_OWNER_ROLE_CODES.has(token))

const normalizeAuditGuid = (value: unknown) => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return /^[0-9a-fA-F-]{36}$/.test(trimmed) ? trimmed.toLowerCase() : null
}

export const resolveAuditAccessibleLocationIds = (
  user?: Record<string, any> | null,
  roles?: Array<Record<string, any>> | null,
) => {
  const results = new Set<string>()
  const roleRows = Array.isArray(roles) ? roles : []

  const locationScopedRoleRows = roleRows.filter((role) => {
    const code = normalizeAuditToken(role?.code)
    return code ? AREA_OWNER_ROLE_CODES.has(code) : false
  })

  if (locationScopedRoleRows.length === 0) {
    ;[
      user?.locationIds,
      user?.location_ids,
      user?.workAreaIds,
      user?.work_area_ids,
      user?.accessibleLocationIds,
      user?.accessible_location_ids,
      user?.locations,
    ].forEach((candidate) => {
      if (!Array.isArray(candidate)) return
      candidate.forEach((item) => {
        const normalized = normalizeAuditGuid(typeof item === 'string' ? item : item?.locationId ?? item?.id)
        if (normalized) results.add(normalized)
      })
    })
  }

  locationScopedRoleRows.forEach((role) => {
    const normalized =
      normalizeAuditGuid(role?.departmentId) ??
      normalizeAuditGuid(role?.department_id) ??
      normalizeAuditGuid(role?.locationId) ??
      normalizeAuditGuid(role?.location_id)
    if (normalized) {
      results.add(normalized)
    }
  })

  return results
}

export const isAuditSafetyRole = (tokens: Set<string>) =>
  Array.from(tokens).some((token) => {
    if (token === '10001' || token === '20001' || token === 'admin' || token === 'safety') {
      return true
    }

    return token.includes('safety') || token.includes('admin') || token.includes('hsse') || token.includes('hse')
  })

export const matchesAuditIdentity = (value: unknown, tokens: Set<string>) => {
  const normalized = normalizeAuditToken(value)
  return normalized ? tokens.has(normalized) : false
}

export const matchesAuditPlant = (value: unknown, tokens: Set<string>) => {
  const normalized = normalizeAuditToken(value)
  return normalized ? tokens.has(normalized) : false
}
